// Copy of this boilerplate exists in localization folders for other partners/packages, and in base-strings.
// Any improvements here should be duplicated across all of those.

import { combineLanguageAndScript, MarketInfo } from "@gamedev-web/gamedev-sdk-core";

type ResourceStrings = Record<string, (...args: string[]) => string>;

/**
 * Lazily loads the Base resource string files for the specified market.
 * @param {MarketInfo} marketInfo Market configuration for the request
 * @returns {Promise<ResourceStrings>} Base resource strings message functions
 */
export const getResourceStringsAsync = async (marketInfo: MarketInfo): Promise<ResourceStrings> => {
  let strings: ResourceStrings;
  // First try to load assets with full locale name, e.g. en-US or sr-RS
  try {
    if (marketInfo.localeWithScript === "en-US") {
      strings = (await import("./strings/strings.resjson")).default as ResourceStrings;
    } else {
      strings = (await import(`./strings/strings.${marketInfo.localeWithScript}.resjson`)).default;
    }
  } catch (e) {
    try {
      // Try to load language only e.g. en or sr-Latn
      let language = marketInfo.language;
      if (marketInfo.script) {
        language = combineLanguageAndScript(marketInfo.language, marketInfo.script);
      }

      strings = (await import(`./strings/strings.${language}.resjson`)).default;
    } catch (innerException) {
      // Try to load language only without script, e.g. sr
      if (marketInfo.script) {
        try {
          strings = (await import(`./strings/strings.${marketInfo.language}.resjson`)).default;
        } catch (innerInnerException) {
          // Fallback to default strings for script languages
          strings = (await import("./strings/strings.resjson")).default as ResourceStrings;
        }
      } else {
        // Fallback to default strings for non-script languages
        strings = (await import("./strings/strings.resjson")).default as ResourceStrings;
      }
    }
  }

  return strings;
};
